import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Scale, Satellite, CreditCard, CheckCheck, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";

const TYPE_META = {
    prazo: { icon: BellRing, cls: "bg-mineral/15 text-mineralHover border-mineral/30", label: "Prazo" },
    monitoramento: { icon: Satellite, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Monitoramento" },
    legislacao: { icon: Scale, cls: "bg-petroleum/10 text-petroleum border-petroleum/20", label: "Legislação" },
    pacote: { icon: CreditCard, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Assinatura" },
};
const FALLBACK = { icon: Bell, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Aviso" };

const notifLink = (n) => n.link || ({ prazo: "/prazos", monitoramento: "/processos", legislacao: "/legislacao", pacote: "/plano" }[n.type] || "/dashboard");

export default function Notifications() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => api.get("/notifications").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const open = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* noop */ }
        navigate(notifLink(n));
    };

    const markAll = async () => { await api.post("/notifications/read-all"); load(); };

    return (
        <div className="space-y-6" data-testid="notifications-page">
            <PageHeader
                title="Notificações"
                subtitle="Todas as suas notificações. Clique em qualquer uma para ir à página correspondente."
                action={items.some((n) => !n.read) ? (
                    <button onClick={markAll} data-testid="mark-all-read" className="inline-flex items-center gap-2 text-sm font-semibold text-petroleum hover:text-petroleumHover transition-colors">
                        <CheckCheck className="w-4 h-4" /> Marcar todas como lidas
                    </button>
                ) : null}
            />

            {loading ? null : items.length === 0 ? (
                <EmptyState icon={Bell} title="Nenhuma notificação" desc="Você será avisado aqui sobre novos atos no DOU, prazos, legislação e mais." />
            ) : (
                <div className="bg-white border border-slate-200 rounded-md shadow-sm divide-y divide-slate-100" data-testid="notifications-list">
                    {items.map((n) => {
                        const meta = TYPE_META[n.type] || FALLBACK;
                        const Icon = meta.icon;
                        return (
                            <button type="button" key={n.id} onClick={() => open(n)} data-testid={`notification-row-${n.id}`}
                                className={`w-full text-left flex items-start gap-3 px-4 py-4 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-mineral/5"}`}>
                                <span className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border ${meta.cls}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-slate-800 truncate">{n.title}</span>
                                        {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-mineral" data-testid={`unread-dot-${n.id}`} />}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-0.5">{n.body}</div>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${meta.cls}`}>{meta.label}</span>
                                        <span className="text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString("pt-BR")}</span>
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-1" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
